// src/components/editable/AddClinicalDataButton.tsx
// -----------------------------------------------------------------------------
// Floating (+) button that opens a searchable, grouped list of every field,
// table, and event type across all domains.  Selecting an item auto-creates
// the parent table record if needed (and auto-activates the domain).
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Fab,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import { fetchData, createData } from "api/apiHelpers";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";
import { athenaColors } from "../../theme/palette";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FieldChoice {
  value: string | number | null;
  display_name: string;
}

interface FieldInfo {
  type: string;
  choices?: FieldChoice[];
  help_text?: string;
  verbose_name?: string;
  required?: boolean;
}

interface SchemaEntry {
  domain_key: string;
  domain_label: string;
  table_type: string;
  table_label: string;
  is_universal: boolean;
  fields: Record<string, FieldInfo>;
}

/** Flat item for the searchable list */
interface ClinicalDataItem {
  /** Unique key for React */
  key: string;
  /** What kind of item — "table" creates an empty record, "field" targets a specific field */
  kind: "table" | "field" | "event_type";
  /** Display name (field verbose_name or table label) */
  label: string;
  /** Help text / description from the model */
  helpText?: string;
  /** Disambiguation line: "Skin Cancer · Diagnosis" or "Patient Profile" */
  context: string;
  /** Domain key (e.g. "medical.cancer.skin") */
  domainKey: string;
  /** Table type (e.g. "statictable", "eventtable") */
  tableType: string;
  /** Whether this is a universal table */
  isUniversal: boolean;
  /** Field name (for kind=field) or event type value (for kind=event_type) */
  fieldName?: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AddClinicalDataButtonProps {
  /** Patient ID — required for creating records */
  patientId: string | number;
  /** Currently selected domain keys */
  selectedDomains: string[];
  /** Callback to add a domain to the selection (auto-activate) */
  onDomainAdd: (domainKey: string) => void;
  /** Callback after a table record is created — triggers a data refresh */
  onRecordCreated: () => void;
  /** Callback with info about which table was just created (for force-visible logic) */
  onTableCreated?: (info: { domainKey: string; tableType: string; isUniversal: boolean }) => void;
  /** Whether to render as a large centered placeholder or a compact footer button */
  variant: "empty-state" | "footer";
}

// ---------------------------------------------------------------------------
// Schema cache (module-level singleton)
// ---------------------------------------------------------------------------

let _schemasCache: SchemaEntry[] | null = null;
let _schemasFetching = false;
const _schemasWaiters: Array<(schemas: SchemaEntry[]) => void> = [];

async function fetchAllSchemas(): Promise<SchemaEntry[]> {
  if (_schemasCache) return _schemasCache;

  return new Promise((resolve) => {
    _schemasWaiters.push(resolve);
    if (_schemasFetching) return; // another caller is already fetching
    _schemasFetching = true;

    fetchData<{ schemas: SchemaEntry[] }>("/all-field-schemas/")
      .then((data) => {
        _schemasCache = data.schemas;
        _schemasWaiters.forEach((w) => w(data.schemas));
        _schemasWaiters.length = 0;
      })
      .catch((err) => {
        logger.error("[AddClinicalData] Failed to fetch schemas:", err);
        _schemasWaiters.forEach((w) => w([]));
        _schemasWaiters.length = 0;
      })
      .finally(() => {
        _schemasFetching = false;
      });
  });
}

// ---------------------------------------------------------------------------
// Helper: build API endpoint string (mirrors EditableTable logic)
// ---------------------------------------------------------------------------

function getApiEndpoint(domainKey: string, tableType: string): string {
  const isUniversal =
    tableType === "patientprofiletable" ||
    tableType === "molecularprofiletable" ||
    tableType === "hematologicalprofiletable" ||
    tableType === "cancerprofiletable" ||
    tableType === "cancerobservationtable";
  if (isUniversal) return `/${tableType}`;
  const urlDomain = domainKey.replace(/\./g, "-");
  return `/${urlDomain}-${tableType}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddClinicalDataButton: React.FC<AddClinicalDataButtonProps> = ({
  patientId,
  selectedDomains,
  onDomainAdd,
  onRecordCreated,
  onTableCreated,
  variant,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [schemas, setSchemas] = useState<SchemaEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input (1.5 s) to avoid freezing on single-letter queries
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, search.length <= 2 ? 1500 : 400);           // longer debounce for short queries
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  // Fetch schemas on first open
  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch("");
    if (!_schemasCache) {
      setLoading(true);
      fetchAllSchemas().then((s) => {
        setSchemas(s);
        setLoading(false);
      });
    } else {
      setSchemas(_schemasCache);
    }
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Auto-focus search on open
  useEffect(() => {
    if (anchorEl) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [anchorEl]);

  // Build flat searchable list from schemas
  const items: ClinicalDataItem[] = useMemo(() => {
    const result: ClinicalDataItem[] = [];

    for (const schema of schemas) {
      const ctx = schema.is_universal
        ? schema.table_label
        : `${schema.domain_label} · ${schema.table_label}`;

      // Add a "table" entry (create entire table)
      result.push({
        key: `table:${schema.domain_key}:${schema.table_type}`,
        kind: "table",
        label: schema.is_universal ? schema.table_label : `${schema.domain_label} - ${schema.table_label}`,
        helpText: t("add_data.create_table_hint", "Create an empty table to start entering data"),
        context: schema.is_universal ? "Universal" : schema.domain_label,
        domainKey: schema.domain_key,
        tableType: schema.table_type,
        isUniversal: schema.is_universal,
      });

      // Add individual fields
      for (const [fieldName, info] of Object.entries(schema.fields)) {
        // For event tables, if the field is "event_type" and it has choices,
        // emit one item per choice (event types like "surgery", "imaging", etc.)
        if (schema.table_type === "eventtable" && fieldName === "event_type" && info.choices) {
          for (const choice of info.choices) {
            result.push({
              key: `event:${schema.domain_key}:${choice.value}`,
              kind: "event_type",
              label: String(choice.display_name),
              helpText: info.help_text || "",
              context: `${schema.domain_label} · Event`,
              domainKey: schema.domain_key,
              tableType: schema.table_type,
              isUniversal: false,
              fieldName: String(choice.value),
            });
          }
          continue;
        }

        result.push({
          key: `field:${schema.domain_key}:${schema.table_type}:${fieldName}`,
          kind: "field",
          label: info.verbose_name || fieldName.replace(/_/g, " "),
          helpText: info.help_text || "",
          context: ctx,
          domainKey: schema.domain_key,
          tableType: schema.table_type,
          isUniversal: schema.is_universal,
          fieldName,
        });
      }
    }

    return result;
  }, [schemas, t]);

  // Filter items by debounced search (avoids UI freeze on single-char queries)
  const filteredItems = useMemo(() => {
    if (!debouncedSearch.trim()) {
      // When not searching, show only table-level items (compact view)
      return items.filter((i) => i.kind === "table");
    }
    const q = debouncedSearch.toLowerCase();
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        i.context.toLowerCase().includes(q) ||
        (i.helpText && i.helpText.toLowerCase().includes(q))
    );
  }, [items, debouncedSearch]);

  // Group filtered items by context for display
  const groupedItems = useMemo(() => {
    const groups: Record<string, ClinicalDataItem[]> = {};
    for (const item of filteredItems) {
      const grp = item.isUniversal ? "Universal Tables" : item.context;
      if (!groups[grp]) groups[grp] = [];
      groups[grp].push(item);
    }
    // Sort: Universal first, then alphabetical
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "Universal Tables") return -1;
      if (b === "Universal Tables") return 1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((key) => ({ group: key, items: groups[key] }));
  }, [filteredItems]);

  // Handle item selection: create the record and refresh
  const handleSelect = useCallback(
    async (item: ClinicalDataItem) => {
      if (!patientId) return;
      setCreating(true);

      try {
        // Auto-activate domain if not selected
        if (!item.isUniversal && !selectedDomains.includes(item.domainKey)) {
          onDomainAdd(item.domainKey);
          // Small delay to let domain selection propagate
          await new Promise((r) => setTimeout(r, 300));
        }

        const endpoint = getApiEndpoint(item.domainKey, item.tableType);

        if (item.kind === "table") {
          // Create an empty record for this table
          await createData(endpoint + "/", { patient: patientId });
        } else if (item.kind === "event_type") {
          // Create an event with the specified type
          await createData(endpoint + "/", {
            patient: patientId,
            event_type: item.fieldName,
          });
        } else if (item.kind === "field") {
          // Create a record with the field initialized to null
          await createData(endpoint + "/", {
            patient: patientId,
            [item.fieldName!]: null,
          });
        }

        // Notify parent which table was created (for force-visible logic)
        onTableCreated?.({
          domainKey: item.domainKey,
          tableType: item.tableType,
          isUniversal: item.isUniversal,
        });

        // Close the popover and refresh data
        handleClose();
        onRecordCreated();
      } catch (err: any) {
        logger.error("[AddClinicalData] Failed to create record:", err);
      } finally {
        setCreating(false);
      }
    },
    [patientId, selectedDomains, onDomainAdd, onRecordCreated, onTableCreated, handleClose]
  );

  const open = Boolean(anchorEl);

  // -------------------------------------------------------------------------
  // Render: Empty state (centered placeholder)
  // -------------------------------------------------------------------------
  // NOTE: The centered (+) button was removed — the top-right "+" button
  // (variant="footer") and the sidebar are now the canonical entry points for
  // adding clinical data. The commented block below is preserved in case we
  // want to reintroduce a centered action trigger later.
  if (variant === "empty-state") {
    return (
      <>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          {/*
          <Fab
            data-tour="add-clinical-data"
            size="small"
            onClick={handleOpen}
            sx={{
              width: 30,
              height: 30,
              minHeight: 30,
              borderRadius: "50%",
              background: "#0b468c",
              color: "#fff",
              border: "none",
              boxShadow: "0 2px 8px rgba(11, 70, 140, 0.35)",
              transition: "all .2s ease",
              "&:hover": {
                background: "rgba(11, 70, 140, 0.85)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(11, 70, 140, 0.4)",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Fab>
          */}
          <Typography
            variant="body2"
            sx={{ color: theme.custom?.contentText?.secondary || theme.palette.text.secondary, textAlign: "center" }}
          >
            {t("add_data.empty_hint", "No clinical data yet. Use the sidebar to add new records.")}
          </Typography>
        </Box>
        {renderPopover()}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Footer button (when tables exist)
  // -------------------------------------------------------------------------
  return (
    <>
      <Fab
        data-tour="add-clinical-data"
        size="small"
        onClick={handleOpen}
        sx={{
          width: 30,
          height: 30,
          minHeight: 30,
          borderRadius: "50%",
          background: "#0b468c",
          color: "#fff",
          border: "none",
          boxShadow: "0 2px 8px rgba(11, 70, 140, 0.35)",
          transition: "all .2s ease",
          "&:hover": {
            background: "rgba(11, 70, 140, 0.85)",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(11, 70, 140, 0.4)",
          },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} />
      </Fab>
      {renderPopover()}
    </>
  );

  // -------------------------------------------------------------------------
  // Popover with search + grouped list
  // -------------------------------------------------------------------------
  function renderPopover() {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 10002 }} // Above tutorial overlay (9999) so (+) is usable during onboarding
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            borderRadius: 1.5,
            backgroundColor: athenaColors.deepNavy,
            border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
            boxShadow: `0 12px 40px ${alpha(athenaColors.deepNavy, 0.5)}`,
            color: alpha(athenaColors.white, 0.85),
          },
        }}
      >
        {/* Header + Search */}
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              mb: 1,
              color: alpha(athenaColors.white, 0.85),
              letterSpacing: 0.5,
            }}
          >
            {t("add_data.title", "Add Clinical Data")}
          </Typography>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder={t("add_data.search_placeholder", "Search tables, fields, events...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: alpha(athenaColors.white, 0.5) }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: alpha(athenaColors.white, 0.06),
                "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
                "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
                "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
              },
              "& .MuiOutlinedInput-input": {
                color: alpha(athenaColors.white, 0.9),
                "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
              },
            }}
          />
          {search.trim() && (
            <Typography
              variant="caption"
              sx={{ color: alpha(athenaColors.white, 0.55), mt: 0.5, display: "block" }}
            >
              {search !== debouncedSearch
                ? t("add_data.typing", "Searching...")
                : `${filteredItems.length} ${t("add_data.results", "results")}`}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: alpha(athenaColors.periwinkle, 0.2) }} />

        {/* Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : groupedItems.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ p: 3, textAlign: "center", color: alpha(athenaColors.white, 0.55) }}
          >
            {t("add_data.no_results", "No matching items found")}
          </Typography>
        ) : (
          <List
            dense
            onWheelCapture={(e) => e.stopPropagation()}
            sx={{
              maxHeight: 420,
              overflow: "auto",
              py: 0,
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(athenaColors.periwinkle, 0.3),
                borderRadius: 3,
              },
            }}
          >
            {groupedItems.map(({ group, items: groupItems }) => (
              <React.Fragment key={group}>
                <ListSubheader
                  sx={{
                    backgroundColor: alpha(athenaColors.periwinkle, 0.1),
                    lineHeight: "28px",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: alpha(athenaColors.white, 0.6),
                  }}
                >
                  {group}
                </ListSubheader>
                {groupItems.map((item) => (
                  <ListItemButton
                    key={item.key}
                    onClick={() => handleSelect(item)}
                    disabled={creating}
                    sx={{
                      py: 0.25,
                      minHeight: 28,
                      px: 2,
                      "&:hover": {
                        backgroundColor: alpha(athenaColors.periwinkle, 0.12),
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {item.kind === "table" && (
                            <TableChartIcon
                              sx={{ fontSize: 14, color: athenaColors.periwinkle, flexShrink: 0 }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={item.kind === "table" ? 600 : 400}
                            sx={{ color: alpha(athenaColors.white, 0.88) }}
                          >
                            {item.label}
                          </Typography>
                          {item.kind === "event_type" && (
                            <Chip
                              label="Event"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.6rem",
                                backgroundColor: alpha(theme.palette.warning.main, 0.25),
                                color: theme.palette.warning.light,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {item.kind !== "table" && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: alpha(athenaColors.white, 0.55),
                                display: "block",
                                lineHeight: 1.3,
                              }}
                            >
                              {item.context}
                            </Typography>
                          )}
                          {item.helpText && item.helpText.length > 0 && item.kind !== "table" && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: alpha(athenaColors.white, 0.4),
                                display: "block",
                                lineHeight: 1.2,
                                fontStyle: "italic",
                                mt: 0.25,
                              }}
                            >
                              {item.helpText.length > 80 ? item.helpText.slice(0, 80) + "…" : item.helpText}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Creating indicator */}
        {creating && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
            }}
          >
            <CircularProgress size={16} sx={{ color: athenaColors.periwinkle }} />
            <Typography variant="caption" sx={{ color: alpha(athenaColors.white, 0.65) }}>
              {t("add_data.creating", "Creating...")}
            </Typography>
          </Box>
        )}
      </Popover>
    );
  }
};

export default AddClinicalDataButton;
